import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Mail, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Clinic-specific success page. Verifies payment with Square via backend
// before showing confirmation.
export default function ClinicSuccess() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const regId = params.get("reg") || "";
  const [status, setStatus] = useState("verifying");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.post(`/clinics/${slug}/verify-payment?reg=${regId}`);
        if (!cancelled) {
          if (data.status === "confirmed") setStatus("confirmed");
          else if (attempts >= 6) setStatus("pending");
          else setAttempts((n) => n + 1);
        }
      } catch {
        if (!cancelled) setStatus("pending");
      }
    };
    if (regId && status === "verifying") {
      check();
      const t = setTimeout(check, 2500);
      return () => { cancelled = true; clearTimeout(t); };
    }
  }, [regId, slug, attempts, status]);

  const whatsAppHref = `https://wa.me/16475132225?text=${encodeURIComponent(
    "Hi Jean Claude! I just registered for Power the Paddle — excited to get started!"
  )}`;

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative" data-testid="clinic-success-page">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#005EEA]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-[#005EEA]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs font-mono-accent uppercase tracking-[0.2em] mb-12 transition-colors">
          <ArrowLeft size={14} /> Back to home
        </Link>

        {status === "verifying" && (
          <div className="text-center py-20">
            <Loader2 className="mx-auto text-[#005EEA] animate-spin mb-6" size={48} />
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-3">— VERIFYING PAYMENT</div>
            <p className="text-white/60">Hold tight — we're confirming your payment with Square...</p>
          </div>
        )}

        {status === "confirmed" && (
          <>
            <motion.div
              initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
              className="mb-8"
            >
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 bg-[#005EEA] rounded-full blur-2xl opacity-60" />
                <div className="relative w-24 h-24 bg-[#005EEA] rounded-full grid place-items-center shadow-[0_0_60px_rgba(0,94,234,0.6)]">
                  <CheckCircle2 size={48} strokeWidth={2.5} className="text-white" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-center mb-6"
            >
              <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— REGISTRATION CONFIRMED</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
                You&apos;re <span className="text-[#005EEA]">in the lab.</span>
              </h1>
              <p className="text-white/60 mt-6 max-w-md mx-auto">
                Your spot in Power the Paddle is locked in. A confirmation email with your
                waiver copies and session details is on its way.
              </p>
            </motion.div>

            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 border border-white/10 bg-black/40 backdrop-blur-sm p-6 sm:p-8"
            >
              <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.3em] mb-4">WHAT HAPPENS NEXT</p>
              <ul className="space-y-3 text-sm text-white/85">
                <li className="flex gap-3"><span className="text-[#005EEA] font-mono-accent text-xs">01</span><span>Check your inbox for the Square payment receipt + GFWJC confirmation email.</span></li>
                <li className="flex gap-3"><span className="text-[#005EEA] font-mono-accent text-xs">02</span><span>Coach Jean Claude will reach out before Week 1 with intake details, the ALKAME waiver, and what to bring.</span></li>
                <li className="flex gap-3"><span className="text-[#005EEA] font-mono-accent text-xs">03</span><span>Clinic begins <strong className="text-white">Tuesday, November 10, 2026</strong> at <strong className="text-white">5:30 PM</strong>. Save the date.</span></li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              <a href={whatsAppHref} target="_blank" rel="noopener noreferrer"
                className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#25D366] text-black py-4 px-6 hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(37,211,102,0.35)]">
                <MessageCircle size={14} strokeWidth={2.5} fill="currentColor" /> Say hi on WhatsApp
              </a>
              <Link to="/" className="font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/20 text-white py-4 px-6 hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
                <Mail size={14} /> Back to Site
              </Link>
            </motion.div>
          </>
        )}

        {status === "pending" && (
          <div className="text-center py-20">
            <div className="font-mono-accent text-xs text-yellow-400 tracking-[0.3em] mb-3">— PENDING CONFIRMATION</div>
            <h2 className="font-display text-3xl uppercase mb-4">Payment processing</h2>
            <p className="text-white/60 max-w-md mx-auto mb-6">
              Your payment is being processed. You&apos;ll receive a confirmation email within a few minutes.
              If anything looks off, message Coach JC on WhatsApp.
            </p>
            <a href={whatsAppHref} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-black py-3 px-6 font-mono-accent text-xs uppercase tracking-wider">
              <MessageCircle size={14} fill="currentColor" strokeWidth={2.5} /> Message Coach JC
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
